/**
 * Everything drawn OVER the map while the guide is leading.
 *
 * All of it is ordinary DOM rather than `HtmlMarker`s, because every piece here
 * is anchored to the SCREEN and not to a coordinate: a pill at the top, a list
 * down the side, two buttons in the corners. The map itself is drawn by
 * `MapLive`, which is the only writer — so «who moved the camera» has one
 * answer and this file has no access to the map at all.
 *
 * It is also the navigation session's engine room: this is where the watch is
 * read, where a fix meets the automaton, and where a deferred event is let go
 * once the turn in flight has ended. Those live together because they share one
 * lifetime — the mode being on — and putting them in the store would make the
 * store subscribe to React.
 *
 * **Tapping a stop centres nothing and SENDS nothing.** Moving around your own
 * map is not a remark to the guide; the only control here that starts a run is
 * «Стоп» (design §2.3).
 */

import { useEffect, useMemo } from 'react';

import { useChat } from '../store/chatStore';
import { useSession } from '../store/sessionStore';
import { cn } from '../lib/cn';
import { stripEmoji as plain } from '../lib/emoji';
import { useNav } from './navStore';
import type { NavCard } from './navMode';
import { focusBoardCard } from '../board/focus';
import { minutesLabel, nextStopIndex, routeModel, STAIRWAY, totalsLabel } from './track';
import { useWatch } from './useWatch';

export default function NavOverlay({ card }: { card: NavCard }) {
  const props = card.props;
  const route = useMemo(() => routeModel(props), [props]);

  const watching = useNav((state) => state.watching);
  const following = useNav((state) => state.following);
  const stopping = useNav((state) => state.stopping);
  const track = useNav((state) => state.track);
  const last = useNav((state) => state.last);
  const chatStatus = useChat((state) => state.status);

  const { state: watchState, last: fix } = useWatch(watching);
  // The REFUSAL is remembered by the session, not by the watch: a denial turns
  // `watching` off, and the hook then reports `idle` — which would erase the one
  // thing the visitor needs to be told.
  const geoState = useSession((state) => state.geoState);
  const denied = geoState === 'denied';

  // A fix is only ever read here, and only through the automaton.
  useEffect(() => {
    if (!fix || !route) return;
    useNav.getState().ingest(fix, route);
  }, [fix, route]);

  // The board is the authority on how far the walk has got; the local set is
  // for immediacy. `current` is the `n` being walked TOWARDS, so everything
  // below it has been reached — one number, no second list to disagree with it.
  const current = props.current;
  useEffect(() => {
    if (route) useNav.getState().syncCurrent(route, current);
  }, [route, current]);

  // An event held back because a turn was running goes as soon as one ends.
  useEffect(() => {
    if (chatStatus === 'idle') useNav.getState().flush();
  }, [chatStatus]);

  if (!route) return null;

  // Read off `arrived`, never off `track.nextStop`: that field is `null` both
  // for «the walk is done» and for «no fix has been ingested yet», and the
  // overlay opens in the second one — which is how a walk with three stops
  // ahead announced «Все точки маршрута пройдены».
  const nextIndex = nextStopIndex(track, route);
  const nextStop = nextIndex !== null ? route.stops[nextIndex] : undefined;
  // A distance needs a position. Without one the pill still says WHERE the
  // guide is leading and asks for the one thing missing.
  const located = track.snap !== null && last !== null;
  // The leg being walked RIGHT NOW is the one arriving at the next stop.
  const leg = nextIndex !== null ? route.legs[nextIndex] ?? null : null;
  // MINUTES ARE THE ROUTER'S OR NOBODY'S, and `source` is what decides which
  // (`track.minutesLabel`): an estimate off a walking pace prints nothing.
  const minutes = minutesLabel(leg);
  // The nearest this wire gets to turn-by-turn: 2GIS gives pedestrians only
  // begin/end manoeuvres, and a flight of steps is what a visitor with a
  // pushchair has to know BEFORE they are standing at the bottom of it.
  const stairs = leg?.style === STAIRWAY;
  // The whole walk, for somebody deciding whether they still have the time.
  const totals = totalsLabel(props);

  return (
    <div className="nav-overlay">
      <div className="nav-overlay__top">
        {!nextStop ? (
          // Only when every stop is behind them — `current` past the last `n`,
          // or the automaton having marked them all.
          <p className="nav-hint">Все точки маршрута пройдены</p>
        ) : located ? (
          <p className="nav-hint">
            До «{nextStop.label}»
            {track.toNextM !== null ? (
              <b> {track.toNextExact ? '' : '~'}{formatMetres(track.toNextM)}</b>
            ) : null}
            {minutes ? <span> · {minutes}</span> : null}
            {stairs ? <span className="nav-hint__stairs"> · лестница</span> : null}
          </p>
        ) : (
          <p className="nav-hint">
            {track.arrived.length === 0 ? 'Первая точка' : 'Следующая точка'}: «
            {nextStop.label}»
            <span className="nav-hint__quiet">
              {' '}
              · {denied ? 'геопозиция закрыта' : 'включите геопозицию'}
            </span>
          </p>
        )}
        <button
          type="button"
          className="nav-btn nav-btn--stop"
          onClick={() => useNav.getState().requestStop()}
          disabled={stopping && chatStatus !== 'idle'}
        >
          {stopping && chatStatus !== 'idle' ? '…' : 'Стоп'}
        </button>
      </div>

      {track.offRoute ? (
        <p className="nav-warn" role="status">
          Кажется, вы ушли с маршрута
        </p>
      ) : null}

      <ol className="nav-stops">
        {route.stops.map((stop, index) => {
          const done = track.arrived.includes(stop.id);
          const current = index === nextIndex;
          return (
            <li key={stop.id}>
              <button
                type="button"
                className={cn(
                  'nav-stops__item',
                  done && 'nav-stops__item--done',
                  current && 'nav-stops__item--current',
                )}
                // Moving around your own map is not a remark to the guide, so
                // this SENDS NOTHING: the camera goes to the stop, and where the
                // pin names a card the board already carries, that card is
                // scrolled to as well. No run either way.
                onClick={() => {
                  useNav.getState().requestCentre(stop.at);
                  if (stop.ref) focusBoardCard(stop.ref);
                }}
              >
                <span className="nav-stops__n">{stop.n}</span>
                <span className="nav-stops__label">{plain(stop.label)}</span>
                <span className="nav-stops__state">
                  {done ? 'пройдено' : current ? 'сейчас' : 'впереди'}
                </span>
              </button>
            </li>
          );
        })}
        {/* The walk's own totals, as the LAST ROW of the list rather than a
            pill of their own: floating under the column they overlapped a pin
            at the left edge, and inside the list they cannot reach the map at
            all. On a phone the list is a horizontal strip and this is simply
            its last chip. */}
        {totals ? (
          <li className="nav-stops__totals">
            <span className="nav-stops__label">Весь маршрут</span>
            <span className="nav-stops__state">{totals}</span>
          </li>
        ) : null}
      </ol>

      <div className="nav-overlay__bottom">
        {props.dgis_url ? (
          <a
            className="nav-btn nav-btn--link"
            href={props.dgis_url}
            target="_blank"
            rel="noreferrer noopener"
          >
            2ГИС
          </a>
        ) : null}

        {/* After an F5 the watch is gone and iOS will only ask again on a
            GESTURE, so the honest control is a button rather than an autostart
            that silently does nothing (design §2.4). */}
        {!watching ? (
          <button
            type="button"
            className="nav-btn nav-btn--primary"
            onClick={() => useNav.getState().setWatching(true)}
            disabled={denied}
            title={denied ? 'Доступ к геопозиции закрыт в браузере' : undefined}
          >
            {/* «Продолжить» is only honest once there HAS been a position: on
                the first entry into the mode nothing has been led yet, and on
                the way back from an F5 it has. */}
            {last ? 'Продолжить вести' : 'Вести меня'}
          </button>
        ) : (
          <button
            type="button"
            className={cn('nav-btn', following && 'nav-btn--on')}
            onClick={() => useNav.getState().setFollowing(true)}
            disabled={!last}
            title={last ? 'Вернуть карту ко мне' : 'Позиция ещё не определена'}
          >
            Я здесь
          </button>
        )}
      </div>

      {denied ? (
        <p className="nav-warn nav-warn--quiet">
          Доступ к геопозиции закрыт — веду по карте без слежения.
        </p>
      ) : null}
      {watching && watchState === 'asking' && last ? (
        <p className="nav-warn nav-warn--quiet">Позиция потеряна, ищу спутники…</p>
      ) : null}
    </div>
  );
}

function formatMetres(metres: number): string {
  if (metres >= 1000) return `${(metres / 1000).toFixed(1)} км`;
  // Ten-metre steps past a hundred: a GPS fix is not accurate to the metre and
  // printing one says it is.
  return metres >= 100 ? `${Math.round(metres / 10) * 10} м` : `${metres} м`;
}
